import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, Marker } from '@react-google-maps/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Spinner, Text } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';

import { sphericalCosinus } from "../../Library/geodist";
import { StoreContext } from '../../Library/contexts';
import CustomMarker from '../../Components/Atoms/CustomMarker';
import PATHS from '../../Routes/paths';
import { GOOGLE_MAPS_API_KEY } from '../../Library/constants';
import { updateRouteTrip, getLatestRouteTrip } from '../../APIs';
import { RideIcon, RefreshIcon, TargetIcon } from '../../Assets/images/svgs'; // Assuming RefreshIcon exists or use generic
const grnMarker = require('../../Assets/images/svgs/grn-marker.png');
const redMarker = require('../../Assets/images/svgs/red-marker.png');

const libraries = ['places', 'drawing', 'geometry']; 

// Helper Functions
const isValidCoordinate = (lat, lng) => {
    return !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng)) && isFinite(lat) && isFinite(lng);
};

const toRad = (deg) => deg * Math.PI / 180;
const toDeg = (rad) => rad * 180 / Math.PI;

const getBearing = (lat1, lng1, lat2, lng2) => {
    const dLng = toRad(lng2 - lng1);
    const y = Math.sin(dLng) * Math.cos(toRad(lat2));
    const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
              Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const findNearestPointIndex = (path, lat, lng) => {
    let minDst = Infinity;
    let closestIndex = -1;
    for (let i = 0; i < path.length; i++) {
        const dst = Math.sqrt(Math.pow(path[i].lat - lat, 2) + Math.pow(path[i].lng - lng, 2));
        if (dst < minDst) {
            minDst = dst;
            closestIndex = i;
        }
    }
    return closestIndex;
}

const formatDuration = (seconds) => {
    if (!seconds) return '0 s';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    if (h > 0) {
        return `${h} h ${m} m`;
    } else if (m > 0) {
        return `${m} m ${s} s`;
    } else {
        return `${s} s`;
    }
};

const containerStyle = {
  width: '100%',
  height: '100%'
};

const Navigation = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { vehicle, route } = state || {};
    const { userVehicles, setActiveRouteTrip, setDriverPinEnteredAt } = useContext(StoreContext);

    // Stats State for UI
    const [stats, setStats] = useState({
        expectedDist: 0,
        actualDist: 0,
        deviation: 0,
        duration: 0,
        startTime: null,
        currentSpeed: 0
    });
    const [showModal, setShowModal] = useState(false);
    const [isRouteCompleted, setIsRouteCompleted] = useState(false);
    const [showCompletionAlert, setShowCompletionAlert] = useState(false);
    
    // Sheet State
    const [isExpanded, setIsExpanded] = useState(true);

    // Route & Map State
    const [routePath, setRoutePath] = useState([]);
    const [displayedRoutePath, setDisplayedRoutePath] = useState([]); 
    const [vehiclePos, setVehiclePos] = useState(null); 
    const [isAutoCentering, setIsAutoCentering] = useState(true);
    const [mapCenter, setMapCenter] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    
    // Deviation Logic State
    const [hasStartedRoute, setHasStartedRoute] = useState(false);
    const [dashedLine, setDashedLine] = useState([]);
    const [currentDeviationPath, setCurrentDeviationPath] = useState([]);

    // Refs for simulation and accumulation
    const hasStartedRouteRef = useRef(false);
    const currentDeviationPathRef = useRef([]);
    const dashedLineInitializedRef = useRef(false);
    const sequenceStepRef = useRef(0);
    const mockIndexRef = useRef(0);
    const mapRef = useRef(null);

    // Stats Accumulators
    const totalDistRef = useRef(0);
    const deviationDistRef = useRef(0);
    const startTimeRef = useRef(null);
    const tripIdRef = useRef(null); // Track backend Trip ID
    const tripEndedRef = useRef(false);
    const mountedRef = useRef(false);
    const catchUpSocketRef = useRef(null);
    const finishTripRef = useRef(null);

    finishTripRef.current = (status) => {
        if (tripEndedRef.current) return;
        if (status === 'COMPLETED' || status === 2) {
            tripEndedRef.current = true;
            setIsRouteCompleted(true);
            if (setActiveRouteTrip) setActiveRouteTrip(null);
        } else if (status === 'INCOMPLETE' || status === 3) {
            tripEndedRef.current = true;
            if (setActiveRouteTrip) setActiveRouteTrip(null);
            if (setDriverPinEnteredAt) setDriverPinEnteredAt(null);
            navigate(`../${PATHS.SELECT_BUS}`, { replace: true });
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    });
    
    // 1. Route & Trip Initialization Effect
    useEffect(() => {
        if(!route) return;

        if(route.fences && route.fences.length) {
             const path = route.fences
                .map(p => ({ lat: parseFloat(p.clat), lng: parseFloat(p.clong) }))
                .filter(p => isValidCoordinate(p.lat, p.lng));
                
             setRoutePath(path);
             setDisplayedRoutePath(path);
             
             // Calculate Total Distance if not provided
             let totalDistMeters = route.totalDistance || 0;
             if (!totalDistMeters && path.length > 1) {
                 for(let i=0; i<path.length-1; i++) {
                     totalDistMeters += sphericalCosinus(path[i].lat, path[i].lng, path[i+1].lat, path[i+1].lng);
                 }
             }

             // Initialize Stats
             const startT = new Date();
             startTimeRef.current = startT;
             setStats(prev => ({ ...prev, expectedDist: totalDistMeters, startTime: startT }));
        }

        const initializeTrip = () => {
             const currentVehId = vehicle.idx || vehicle.vid;
             const driverId = state?.driverId;
             getLatestRouteTrip({ driverId, vid: currentVehId, routeId: route.idx }, (err, res) => {
                 const data = (res && !Array.isArray(res)) ? res : (Array.isArray(res) && res.length ? res[0] : null);
                 const stateTripId = state?.tripId;

                 if (!err && data) {
                     if (stateTripId && data.idx === stateTripId) {
                         if (data.status === 'COMPLETED' || data.status === 2) setShowCompletionAlert(true);
                         else tripIdRef.current = data.idx;
                     }
                     else if (stateTripId && data.idx !== stateTripId) tripIdRef.current = stateTripId;
                     else {
                         if (data.status === 'COMPLETED' || data.status === 2) setShowCompletionAlert(true);
                         else tripIdRef.current = data.idx;
                     }
                 } else {
                     if (stateTripId) tripIdRef.current = stateTripId;
                     else navigate(`../${PATHS.SELECT_ROUTE}`, { state: { vehicle } });
                 }
             });
        }

        if (!tripIdRef.current) {
            initializeTrip();
        }
    }, [route, vehicle, navigate, state]);

    // 2. Vehicle Initialization Effect  
    useEffect(() => {
        if(!vehicle) {
            navigate(`../${PATHS.SELECT_BUS}`);
            return;
        }

        if (userVehicles && userVehicles.length) {
            const currentVeh = userVehicles.find(v => v.idx == vehicle.idx);
            if (currentVeh && isValidCoordinate(currentVeh.lat, currentVeh.lng)) {
                 setVehiclePos({ 
                     lat: parseFloat(currentVeh.lat), 
                     lng: parseFloat(currentVeh.lng),
                     direction: currentVeh.direction || 0 
                 });
            }
        }
    }, [vehicle, userVehicles, navigate]);

    // 3. Auto-Center Logic
    useEffect(() => {
        if (vehiclePos && isAutoCentering) {
            setMapCenter(vehiclePos);
        }
    }, [vehiclePos, isAutoCentering]);

    // 4. Fit map to show full route on first load (once route + map are both ready)
    useEffect(() => {
        if (!routePath.length || !mapRef.current) return;
        const bounds = new window.google.maps.LatLngBounds();
        routePath.forEach(p => bounds.extend(p));
        if (vehiclePos) bounds.extend(vehiclePos);
        mapRef.current.fitBounds(bounds, { top: 20, right: 20, bottom: 160, left: 20 });
    }, [routePath, mapLoaded]); // re-runs when route data arrives and when map loads

    // 5. Initialize dashed approaching line from known vehicle position (before first WS update)
    useEffect(() => {
        if (!routePath.length || !vehiclePos || hasStartedRouteRef.current || dashedLineInitializedRef.current) return;
        dashedLineInitializedRef.current = true;
        const distToStart = sphericalCosinus(vehiclePos.lat, vehiclePos.lng, routePath[0].lat, routePath[0].lng);
        if (distToStart < 20) {
            setHasStartedRoute(true);
            hasStartedRouteRef.current = true;
            setDashedLine([]);
        } else {
            setDashedLine([vehiclePos, routePath[0]]);
        }
    }, [routePath, vehiclePos]);

    // 3. Tracking & Mocking Effect
    useEffect(() => {
        if(!vehicle || showCompletionAlert) return;

        const subscribeToVehicle = () => {
            if (global.UWS && global.UWS.wsConnected) {
                global.UWS.sendRequest({ 
                    action: global.EE.EVENT_TYPES.SUBSCRIBE_TO_VEHICLES, 
                    data: { veh: [vehicle.idx] } 
                });
                catchUp();
            }
        };

        const catchUp = () => {
            const socket = global.UWS.socket;
            const activeTripId = tripIdRef.current || state?.tripId;
            if (!activeTripId || catchUpSocketRef.current === socket || tripEndedRef.current) return;
            catchUpSocketRef.current = socket;
            getLatestRouteTrip({ driverId: state?.driverId, vid: vehicle.idx || vehicle.vid, routeId: route.idx }, (err, res) => {
                const trip = Array.isArray(res) ? res[0] : res;
                if (!mountedRef.current || tripEndedRef.current || err || !trip || trip.idx !== activeTripId) return;
                finishTripRef.current(trip.status);
            });
        };

        const handleVehicleUpdate = (data) => {
            if (data && data.vid == vehicle.idx) {
                if (isValidCoordinate(data.lat, data.lng)) {
                    // Update Position & Accumulate Distance
                    setVehiclePos(prev => {
                        let newDirection = data.direction;
                        const validLat = parseFloat(data.lat);
                        const validLng = parseFloat(data.lng);
                        
                        if (prev) {
                            // Bearing
                            if (newDirection === undefined) {
                                newDirection = getBearing(prev.lat, prev.lng, validLat, validLng);
                            }
                            
                            // Distance Accumulation
                            const dist = sphericalCosinus(prev.lat, prev.lng, validLat, validLng); // meters
                            if (dist > 0 && dist < 5000) { // Sanity check (skip huge jumps > 5km)
                                const distKm = dist / 1000;
                                totalDistRef.current += distKm;
                                
                                // Deviation Accumulation (if currently deviating)
                                // We check deviation logic below, but we need to know 'state' of deviation.
                                // Using currentDeviationPathRef length as proxy for 'isDeviating'
                                if (currentDeviationPathRef.current.length > 0) {
                                     deviationDistRef.current += distKm;
                                }
                            }
                        }

                        return { 
                            lat: validLat,
                            lng: validLng,
                            direction: newDirection || prev?.direction || 0
                        };
                    });

                    // Update UI Stats
                    const durationSec = startTimeRef.current ? (Date.now() - startTimeRef.current.getTime()) / 1000 : 0;
                    setStats(prev => ({ 
                        ...prev, 
                        currentSpeed: data.spd || 0,
                        actualDist: totalDistRef.current,
                        deviation: deviationDistRef.current,
                        duration: durationSec
                    }));

                    if (routePath.length) {
                        const distToStart = sphericalCosinus(data.lat, data.lng, routePath[0].lat, routePath[0].lng);

                        // Always trim the displayed route to remove the path already travelled,
                        // regardless of whether the route has officially "started".
                        const nearestIdx = findNearestPointIndex(routePath, data.lat, data.lng);
                        if (nearestIdx > 0) {
                            setDisplayedRoutePath(routePath.slice(nearestIdx));
                        }

                        if (!hasStartedRouteRef.current) {
                            // Approaching: show dashed line, wait for vehicle to enter route
                            if (distToStart < 20) {
                                setHasStartedRoute(true);
                                hasStartedRouteRef.current = true;
                                setDashedLine([]);
                            } else {
                                setDashedLine([{lat: parseFloat(data.lat), lng: parseFloat(data.lng)}, routePath[0]]);
                            }
                        } else {
                            // On route: update deviation and check completion
                            const userDistToRoute = nearestIdx !== -1
                                ? sphericalCosinus(data.lat, data.lng, routePath[nearestIdx].lat, routePath[nearestIdx].lng)
                                : Infinity;

                            // Deviation Check
                            if (userDistToRoute > 20) {
                                const newPoint = {lat: parseFloat(data.lat), lng: parseFloat(data.lng)};
                                setCurrentDeviationPath(prev => {
                                    const newVal = [...prev, newPoint];
                                    currentDeviationPathRef.current = newVal;
                                    return newVal;
                                });
                            } else if (userDistToRoute < 20 && currentDeviationPathRef.current.length > 0) {
                                setCurrentDeviationPath([]);
                                currentDeviationPathRef.current = [];
                            }
                        }
                    }
                }
            }
        };

        global.EE.addEventListener(global.EE.EVENT_TYPES.UWS_CONNECTED, subscribeToVehicle);
        global.EE.addEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, handleVehicleUpdate);
        global.EE.addEventListener(global.EE.EVENT_TYPES.RT_LOC_EVT, handleVehicleUpdate);

        subscribeToVehicle();

        // --- MOCK SIMULATION ---
        // const mockInterval = setInterval(() => { ... }, 750);
        // const mockInterval = setInterval(() => {
        //     if (routePath.length > 0 && !isCompletedRef.current) {
        //         // Scenario Handling
        //         let point = null;
        //         let nextPoint = null;
        //         let currentIdx = mockIndexRef.current;

        //         // Phase 0: Pre-start (approaching)
        //         if (sequenceStepRef.current === 0) {
        //             const start = routePath[0];
        //             point = { lat: start.lat - 0.005, lng: start.lng - 0.005 };
        //             nextPoint = { lat: start.lat - 0.004, lng: start.lng - 0.004 };
        //             mockIndexRef.current = 0;
        //             sequenceStepRef.current = 1;
        //         }
        //         // Phase 1: Jump to Start
        //         else if (sequenceStepRef.current === 1) {
        //             point = routePath[0];
        //             nextPoint = routePath[1];
        //             mockIndexRef.current = 0;
        //             sequenceStepRef.current = 2; // On Route
        //         }
        //         // Phase 2: On Route
        //         else if (sequenceStepRef.current === 2) {
        //             point = routePath[currentIdx];
        //             nextPoint = routePath[(currentIdx + 1) % routePath.length];
        //             mockIndexRef.current = (currentIdx + 1);
                    
        //             // Trigger deviation at index 5 (arbitrary point)
        //             if (currentIdx === 5) sequenceStepRef.current = 3;
                    
        //             // Trigger Completion at end
        //             if (currentIdx >= routePath.length - 2) {
        //                 // Let it finish
        //             }
        //         }
        //         // Phase 3: Deviation
        //         else if (sequenceStepRef.current === 3) {
        //              const base = routePath[5]; // Deviate from point 5
        //              const deviationOffset = (mockIndexRef.current - 5) * 0.002;
        //              point = { lat: base.lat + deviationOffset, lng: base.lng + deviationOffset };
        //              nextPoint = { lat: base.lat + deviationOffset + 0.002, lng: base.lng + deviationOffset + 0.002 };
                     
        //              mockIndexRef.current++; 
        //              if (mockIndexRef.current > 15) { // deviations
        //                  sequenceStepRef.current = 4; // Return
        //              }
        //         }
        //         // Phase 4: Rejoin
        //         else if (sequenceStepRef.current === 4) {
        //             point = routePath[6]; // Snap back to next point
        //             nextPoint = routePath[7];
        //             mockIndexRef.current = 6;
        //             sequenceStepRef.current = 2; // Resume
        //         }

        //         if(point && nextPoint) {
        //             const bearing = getBearing(point.lat, point.lng, nextPoint.lat, nextPoint.lng);
        //             handleVehicleUpdate({
        //                 vid: vehicle.idx,
        //                 lat: point.lat,
        //                 lng: point.lng,
        //                 spd: Math.floor(Math.random() * 30) + 10,
        //                 direction: bearing
        //             });
        //         }
        //     }
        // }, 750);
        // (Commented out for production behavior)
        // -----------------------

        return () => {
            global.EE.removeEventListener(global.EE.EVENT_TYPES.UWS_CONNECTED, subscribeToVehicle);
            global.EE.removeEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, handleVehicleUpdate);
            global.EE.removeEventListener(global.EE.EVENT_TYPES.RT_LOC_EVT, handleVehicleUpdate);
            
            if (global.UWS && global.UWS.wsConnected) {
                global.UWS.sendRequest({ 
                    action: global.EE.EVENT_TYPES.UNSUBSCRIBE_TO_VEHICLES, 
                    data: { veh: [vehicle.idx] } 
                });
            }
            // clearInterval(mockInterval);
        };
    }, [vehicle, routePath, route, state]);

    useEffect(() => {
        if (!vehicle) return;

        const vid = vehicle.idx || vehicle.vid;
        const handleTripEnd = (d) => {
            if (!d || tripEndedRef.current) return;
            const activeTripId = tripIdRef.current || state?.tripId;
            if (activeTripId ? d.tripId !== activeTripId : String(d.vid) !== String(vid)) return;
            finishTripRef.current(d.status);
        };

        global.EE.addEventListener(global.EE.EVENT_TYPES.TRIP_END, handleTripEnd);
        return () => global.EE.removeEventListener(global.EE.EVENT_TYPES.TRIP_END, handleTripEnd);
    }, [vehicle, state]);

    const onLoad = useCallback(function callback(map) {
        mapRef.current = map;
        setMapLoaded(true);
    }, []);

    const handleChangeRouteClick = () => {
        setShowModal(true);
    }
    
    const onConfirmChangeRoute = () => {
        if (tripIdRef.current) {
            updateRouteTrip({
                tripId: tripIdRef.current,
                driverId: state?.driverId,
                status: 'INCOMPLETE',
                endTs: Date.now(),
                endReason: 'CHANGED_ROUTE',
                endLat: vehiclePos?.lat || 0,
                endLng: vehiclePos?.lng || 0
            }, () => {
                setShowModal(false);
                if (setActiveRouteTrip) setActiveRouteTrip(null);
                if (setDriverPinEnteredAt) setDriverPinEnteredAt(null); // reset 15-min window
                navigate(`../${PATHS.SELECT_BUS}`, { replace: true });
            });
        } else {
            setShowModal(false);
            if (setDriverPinEnteredAt) setDriverPinEnteredAt(null);
            navigate(`../${PATHS.SELECT_BUS}`, { replace: true });
        }
    }

    if (!isLoaded) return <div className="df-loader"><Spinner color='var(--ButtonPrimary)'/></div>;

    // Determine Center: User's mapCenter state OR initial logic
    const initialCenter = routePath.length > 0 ? routePath[0] : { lat: 23.2599, lng: 77.4126 };
    const centerToUse = mapCenter || vehiclePos || initialCenter;

    const handleRecenter = () => {
        if (vehiclePos) {
            setIsAutoCentering(true);
            setMapCenter(vehiclePos);
            mapRef.current?.panTo(vehiclePos);
            mapRef.current?.setZoom(17);
        }
    };

    const fullVehicleDetails = (userVehicles && userVehicles.find(v => v.idx == vehicle.idx)) || vehicle; 
    const markerLat = vehiclePos?.lat || (routePath.length > 0 ? routePath[0].lat : null);
    const markerLng = vehiclePos?.lng || (routePath.length > 0 ? routePath[0].lng : null);

    const currentVehicle = {
        ...fullVehicleDetails,
        ts: fullVehicleDetails.ts || (Date.now() / 1000), 
        vid: fullVehicleDetails.vid || fullVehicleDetails.idx,
        lat: markerLat,
        lng: markerLng,
        spd: stats.currentSpeed,
        direction: vehiclePos?.direction || fullVehicleDetails.direction || 0 
    };

    return (
        <div className="df-page df-navigation">
            <div className="df-map-container">
                 <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={centerToUse}
                    zoom={17}
                    onLoad={onLoad}
                    onDragStart={() => setIsAutoCentering(false)}
                    options={{ mapTypeControl: false, streetViewControl: false, zoomControl: false, fullscreenControl: false }}
                 >
                    {/* Dashed approaching line: vehicle → route start */}
                    {dashedLine.length > 0 && !hasStartedRoute &&
                        <Polyline
                            path={dashedLine}
                            options={{
                                strokeColor: '#FFFFFF',
                                strokeOpacity: 0,
                                icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 4 }, offset: '0', repeat: '20px' }],
                                strokeWeight: 2
                            }}
                        />
                    }
                    {/* Red deviation line */}
                    {currentDeviationPath.length > 0 &&
                        <Polyline
                            path={currentDeviationPath}
                            options={{
                                strokeColor: '#FF3D00',
                                strokeOpacity: 1.0,
                                strokeWeight: 4,
                                zIndex: 3,
                            }}
                        />
                    }
                    {/* Remaining route line */}
                    {displayedRoutePath.length > 0 &&
                        <Polyline
                            path={displayedRoutePath}
                            options={{
                                strokeColor: '#1E88E5',
                                strokeOpacity: 1.0,
                                strokeWeight: 6,
                                zIndex: 2,
                            }}
                        />
                    }
                    {/* Intermediate waypoint dots (stops that haven't been passed yet) */}
                    {displayedRoutePath.length > 2 &&
                        displayedRoutePath.slice(1, displayedRoutePath.length - 1).map((point, i) => (
                            <Marker
                                key={`wp-${i}`}
                                position={point}
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    scale: 5,
                                    fillColor: '#FFFFFF',
                                    fillOpacity: 1,
                                    strokeColor: '#1E88E5',
                                    strokeWeight: 2.5,
                                }}
                                zIndex={4}
                            />
                        ))
                    }
                    {/* Start marker — hidden once vehicle starts moving along the route */}
                    {routePath.length > 0 && displayedRoutePath.length === routePath.length && (
                        <Marker
                            position={routePath[0]}
                            icon={{ url: grnMarker, anchor: new window.google.maps.Point(15, 35), scaledSize: new window.google.maps.Size(32, 32) }}
                            zIndex={5}
                        />
                    )}
                    {/* End / destination marker — hidden once vehicle reaches the final waypoint */}
                    {displayedRoutePath.length > 1 && (
                        <Marker
                            position={routePath[routePath.length - 1]}
                            icon={{ url: redMarker, anchor: new window.google.maps.Point(15, 35), scaledSize: new window.google.maps.Size(32, 32) }}
                            zIndex={5}
                        />
                    )}
                    {/* Vehicle marker */}
                    {currentVehicle.lat && (
                        <CustomMarker
                            vehicle={currentVehicle}
                            vehicleInfo={currentVehicle}
                        />
                    )}
                 </GoogleMap>
            </div>

            {/* Sliding Stats Panel */}
            <motion.div
                className="df-sheet-panel"
                animate={{
                    y: 0 // Ensure it stays fixed
                }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
                {/* Recenter Button (Dynamic Position) */}
                {!isAutoCentering && (
                     <div className="df-recenter-btn" onClick={handleRecenter}>
                        <TargetIcon />
                     </div>
                )}

                <motion.div
                    className="df-sheet-handle-bar"
                    onPanEnd={(e, info) => {
                        // Swipe logic: positive offset = down (collapse), negative = up (expand)
                        if (info.offset.y > 20) setIsExpanded(false);
                        else if (info.offset.y < -20) setIsExpanded(true);
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ touchAction: 'none' }} // Prevent scrolling while dragging handle
                >
                    <div className="df-sheet-handle" />
                </motion.div>

                <motion.div
                    className="df-stats-collapsible"
                    animate={{
                        height: isExpanded ? 'auto' : 0,
                        opacity: isExpanded ? 1 : 0,
                        marginBottom: isExpanded ? 8 : 0
                    }}
                    initial={{ height: 'auto', opacity: 1, marginBottom: 24 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    style={{ overflow: 'hidden' }}
                >
                    <div className="df-stats-grid">
                        <div className="df-stat-item">
                            <label>EXPECTED DIST</label>
                            <div className="df-stat-val">{stats.expectedDist ? (stats.expectedDist / 1000).toFixed(1) : '0.0'} km</div>
                        </div>
                        <div className="df-stat-item">
                            <label>DIST TRAVELLED</label>
                            <div className="df-stat-val">{stats.actualDist.toFixed(1)} km</div>
                        </div>
                        <div className="df-stat-item">
                            <label>DEVIATED DIST</label>
                            <div className="df-stat-val text-red">{stats.deviation.toFixed(2)} km</div>
                        </div>
                        <div className="df-stat-item">
                            <label>TOTAL DURATION</label>
                            <div className="df-stat-val">{formatDuration(stats.duration)}</div>
                        </div>
                        <div className="df-stat-item">
                            <label>ROUTE START TIME</label>
                            <div className="df-stat-val">{stats.startTime ? stats.startTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '--:--'}</div>
                        </div>
                        <div className="df-stat-item">
                            <label>CURRENT SPEED</label>
                            <div className="df-stat-val">{stats.currentSpeed} km/hr</div>
                        </div>
                    </div>
                </motion.div>

                <div className="df-sheet-footer">
                    <div className="df-live-route-card">
                         <div className="df-live-icon-box">
                            <RideIcon />
                         </div>
                         <div className="df-live-info">
                            <label>CURRENT LIVE ROUTE <span className="status-dot green"></span></label>
                            <div className="df-live-route-name">{route?.nm}</div>
                         </div>
                    </div>

                    <Button
                        className="df-change-route-btn-dark"
                        onClick={handleChangeRouteClick}
                    >
                        <RefreshIcon /> CHANGE ROUTE
                    </Button>
                </div>
            </motion.div>
            
            {/* Change Route Modal */}
            {showModal && (
                <div className="df-modal-overlay">
                    <div className="df-modal-content">
                        <Text className="df-modal-title" color="#FF5252" mb={4} fontSize="lg" fontWeight="bold" textAlign="center">
                            You are Changing Current Route
                        </Text>
                        <Text className="df-modal-body" mb={6} textAlign="center" color="white">
                            You haven’t finished this route. Changing it will mark it complete. Are you sure?
                        </Text>
                        <div className="df-modal-actions">
                            <Button 
                                className="df-modal-btn-cancel" 
                                onClick={() => setShowModal(false)}
                                variant="outline"
                                color="white"
                                _hover={{ bg: 'rgba(255,255,255,0.1)' }}
                                borderColor="#3182CE"
                                width="45%"
                            >
                                Don’t Change
                            </Button>
                            <Button 
                                className="df-modal-btn-confirm" 
                                onClick={onConfirmChangeRoute}
                                bg="#3182CE"
                                color="white"
                                _hover={{ bg: '#2B6CB0' }}
                                width="45%"
                            >
                                Change Route
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Route Completed Overlay */}
            {isRouteCompleted && (
                 <div className="df-completion-overlay" style={{
                     position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                     background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
                     display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                 }}>
                     <div className="df-completion-card" style={{
                         background: 'rgba(23, 25, 35, 0.9)', padding: '24px', borderRadius: '16px',
                         maxWidth: '90%', textAlign: 'center', border: '1px solid #4A5568'
                     }}>
                         <Text fontSize="2xl" fontWeight="bold" color="white" mb={4}>Route Completed!</Text>
                         
                         <div className="df-stats-grid mb-6" style={{ gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                             <div className="df-stat-item">
                                 <label>Actual Distance</label>
                                 <div className="df-stat-val large">{stats.actualDist.toFixed(1)} km</div>
                             </div>
                             <div className="df-stat-item">
                                 <label>Deviation</label>
                                 <div className="df-stat-val text-red large">{stats.deviation.toFixed(2)} km</div>
                             </div>
                         </div>

                         <Text color="gray.400" mb={6}>You have successfully completed the route.</Text>

                         <Button 
                            onClick={() => navigate(`../${PATHS.SELECT_BUS}`, { replace: true })}
                             bg="#3182CE" color="white" size="lg" width="100%"
                             _hover={{ bg: '#2B6CB0' }}
                         >
                             Select Bus
                         </Button>
                     </div>
                 </div>
            )}

            {/* Already Completed Trip Alert Modal */}
            {showCompletionAlert && (
                <div className="df-modal-overlay" style={{ backdropFilter: 'blur(5px)', background: 'rgba(0,0,0,0.7)' }}>
                    <div className="df-modal-content">
                        <Text className="df-modal-title" color="#FF5252" mb={4} fontSize="lg" fontWeight="bold" textAlign="center">
                            Route Already Completed
                        </Text>
                        <Text className="df-modal-body" mb={6} textAlign="center" color="white">
                            This trip has explicitly been marked as completed. You cannot resume it.
                        </Text>
                        <div className="df-modal-actions" style={{ justifyContent: 'center' }}>
                            <Button 
                                className="df-modal-btn-confirm" 
                                onClick={() => navigate(`../${PATHS.SELECT_BUS}`)}
                                bg="#3182CE"
                                color="white"
                                _hover={{ bg: '#2B6CB0' }}
                                width="80%"
                            >
                                Select New Route
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navigation;
